import React from "react";

// Shared between the Home Dashboard (renders live announcement banners) and
// the Ops Portal Config Announcements tab (renders the same banners as a
// live preview while editing) — keeping this in one place guarantees the
// preview an admin sees always matches what real users see.

// Severity → presentation. The backend only accepts 'info' | 'warning' | 'error'
// (Pydantic enum) — the pastel palette stays a frontend concern so a restyle
// never needs a data migration.
export const SEVERITY_STYLES = {
  error: { background: "#FEF2F2", color: "#991B1B", label: "Error" },
  warning: { background: "#FFFBEB", color: "#92400E", label: "Warning" },
  info: { background: "#EFF6FF", color: "#1E40AF", label: "Info" },
};

export const alertSx = (severity) => {
  const s = SEVERITY_STYLES[severity] ?? SEVERITY_STYLES.info;
  return {
    fontSize: "0.83rem",
    py: "6px",
    background: s.background,
    border: "none",
    color: s.color,
    "& a": { color: s.color, fontWeight: 600 },
    "& .MuiAlert-icon": { color: s.color },
    "& .MuiAlert-action .MuiIconButton-root": { color: s.color },
  };
};

const getSafeExternalHref = (rawUrl) => {
  const candidate = (rawUrl || "").trim();
  if (!candidate) return null;
  try {
    const parsed = new URL(candidate);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? candidate : null;
  } catch {
    return null;
  }
};

// Markdown-lite renderer: understands [text](url) and **bold** only. Output is
// plain JSX (React escapes everything), so notice content coming from the
// backend can never inject HTML — no dangerouslySetInnerHTML needed. Links are
// restricted to http(s) so a crafted message can't smuggle a javascript: URL.
export const renderNoticeMessage = (message) =>
  (message || "").split(/(\[[^\]]+\]\([^)]+\)|\*\*[^*]+\*\*)/g).map((part, i) => {
    const link = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const safeHref = getSafeExternalHref(link[2]);
      if (!safeHref) return part;
      return (
        <a key={i} href={safeHref} target="_blank" rel="noreferrer">
          {link[1]}
        </a>
      );
    }
    const bold = part.match(/^\*\*([^*]+)\*\*$/);
    if (bold) return <strong key={i}>{bold[1]}</strong>;
    return part;
  });
